const CAPABILITY_PATH = /\/(i|l|c|w)\/[^/?#\s"'<>\\]+/gi;
const FUNCTION_PATH = /(\/(?:share-resolver|invite-resolver|share-unlock)\/)[^/?#\s"'<>\\]+/gi;

const SENSITIVE_KEYS = new Set(['token', 'sharetoken', 'tokenhash', 'accesstoken', 'refreshtoken']);

function fullyDecode(value: string): string {
  let out = value;
  for (let i = 0; i < 3; i += 1) {
    let next: string;
    try {
      next = decodeURIComponent(out);
    } catch {
      break;
    }
    if (next === out) break;
    out = next;
  }
  return out;
}

function normalizedKey(key: string): string {
  return fullyDecode(key).toLowerCase().replace(/[_-]/g, '');
}

function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEYS.has(normalizedKey(key));
}

export function scrubTelemetryString(value: string): string {
  return value
    .replace(CAPABILITY_PATH, '/$1')
    .replace(FUNCTION_PATH, '$1')
    .replace(/([?#&])([^?#&=\s"'<>\\]+)=[^&#\s"']*/g, (match, sep: string, key: string) => (
      isSensitiveKey(key) ? sep : match
    ))
    .replace(/\?&+/g, '?')
    .replace(/&&+/g, '&')
    .replace(/#&+/g, '#')
    .replace(/[?&#]$/g, '');
}

function stringHasSensitiveKey(value: string): boolean {
  let found = false;
  value.replace(/[?#&]([^?#&=\s"'<>\\]+)=/g, (match, key: string) => {
    if (isSensitiveKey(key)) found = true;
    return match;
  });
  return found;
}

export function hrefHasAuthCredential(href: string): boolean {
  try {
    const url = new URL(href, 'https://rdv.invalid');
    const probes = [url.search.replace(/^\?/, ''), url.hash.replace(/^#/, '')];
    return probes.some((probe) => {
      const params = new URLSearchParams(probe);
      for (const key of params.keys()) {
        if (isSensitiveKey(key)) return true;
      }
      return false;
    });
  } catch {
    return stringHasSensitiveKey(href);
  }
}

export function containsCapabilityToken(value: string): boolean {
  return (
    /\/(i|l|c|w)\/[^/?#\s"'<>\\]+/i.test(value) ||
    /\/(?:share-resolver|invite-resolver|share-unlock)\/[^/?#\s"'<>\\]+/i.test(value) ||
    stringHasSensitiveKey(value)
  );
}

function isPlainObject(value: object): boolean {
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function scrubRendered(rendered: unknown, depth: number): unknown {
  if (rendered == null) return rendered;
  const kind = typeof rendered;
  if (kind === 'string' || kind === 'number' || kind === 'boolean' || kind === 'bigint') {
    return scrubTelemetryValue(rendered, depth);
  }
  if (Array.isArray(rendered)) return scrubTelemetryValue(rendered, depth);
  if (kind === 'object' && isPlainObject(rendered)) return scrubTelemetryValue(rendered, depth);
  return undefined;
}

function requestUrl(value: object): string | undefined {
  if (typeof Request !== 'undefined' && value instanceof Request) return value.url;
  if (Object.prototype.toString.call(value) !== '[object Request]') return undefined;
  if (!('url' in value) || typeof value.url !== 'string') return undefined;
  return value.url;
}

export function scrubTelemetryValue<T>(value: T, depth = 0): T {
  if (value == null) return value;
  if (typeof value === 'string') return scrubTelemetryString(value) as T;
  // Past the depth limit, a nested object used to pass through unchanged.
  // Strings are still redacted; everything else is dropped.
  if (depth > 8) return undefined as T;
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    return value.map((item) => scrubTelemetryValue(item, depth + 1)) as T;
  }

  // REN-362: class instances (Sentry Span, Hub, Scope, Map, …) use the
  // [object Object] tag, so a tag check deep-copied their private graph.
  // Only Object.prototype and null-prototype records are walked.
  const url = requestUrl(value);
  if (url !== undefined) return scrubTelemetryString(url) as T;

  const tag = Object.prototype.toString.call(value);
  const dated = value as { toISOString?: () => string };
  if ((value instanceof Date || tag === '[object Date]') && typeof dated.toISOString === 'function') {
    try {
      return scrubTelemetryString(dated.toISOString()) as T;
    } catch {
      return undefined as T;
    }
  }

  if (isPlainObject(value)) {
    const source = value as Record<string, unknown>;
    const next: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(source)) {
      if (isSensitiveKey(key)) continue;
      if (typeof child === 'function') continue;
      next[key] = scrubTelemetryValue(child, depth + 1);
    }
    return next as T;
  }

  const host = value as { toJSON?: () => unknown };
  if (typeof host.toJSON === 'function') {
    try {
      const rendered = host.toJSON();
      if (rendered === value) return undefined as T;
      return scrubRendered(rendered, depth + 1) as T;
    } catch {
      return undefined as T;
    }
  }
  return undefined as T;
}
